import type { ButtonHTMLAttributes, ReactElement } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  isLoading?: boolean;
  /** States the action in progress, e.g. "Saving sale". DESIGN.md forbids
   *  vague loading text when the product knows what is happening. */
  loadingLabel?: string;
}

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-brand-600 text-white hover:bg-brand-700 disabled:bg-surface-sunken disabled:text-fg-muted',
  secondary: 'border border-line bg-surface-raised text-fg hover:bg-surface-sunken',
  ghost: 'text-fg-muted hover:bg-surface-sunken',
};

export function Button({
  variant = 'primary',
  isLoading = false,
  loadingLabel,
  className,
  disabled,
  children,
  ...props
}: ButtonProps): ReactElement {
  return (
    <button
      className={cn(
        'inline-flex h-10 items-center sm:h-10 max-sm:h-11 justify-center rounded-control px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed',
        variantClasses[variant],
        className,
      )}
      disabled={disabled === true || isLoading}
      {...props}
    >
      {isLoading ? (loadingLabel ?? children) : children}
    </button>
  );
}
